import React, { useEffect, useState } from "react";
import axios from "axios";

interface AbandonedCart {
  id: string;
  pairId: string;
  contactDetails: { name?: string; surname?: string; dob?: string; email?: string; phone?: string };
  flightRoutes: any;
  status: string;
  createdAt: string;
}

const AbandonedCarts = () => {
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCart, setSelectedCart] = useState<any>(null);

  // You can set up your base API URL here or from env/config
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  const fetchCarts = async () => {
    setLoading(true);
    try {
      // Pass auth headers if required, assuming it's required for admin endpoints
      const token = localStorage.getItem("token");
      const res = await axios.get(`${baseUrl}/abandoned-cart/admin/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const transformedCarts = res.data.map((c: any) => ({
        ...c,
        contactDetails:
          typeof c.contactDetails === "string"
            ? JSON.parse(c.contactDetails)
            : c.contactDetails,
        flightRoutes:
          typeof c.flightRoutes === "string"
            ? JSON.parse(c.flightRoutes)
            : c.flightRoutes,
      }));
      setCarts(transformedCarts);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCarts();
  }, []);

  
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this cart?")) return;
    setDeleting(id);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${baseUrl}/abandoned-cart/admin/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCarts();
    } catch (e) {
      console.error(e);
      alert("Failed to delete cart");
    }
    setDeleting(null);
  };

  const handleRecover = async (id: string) => {
    setRecovering(id);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${baseUrl}/abandoned-cart/admin/recover`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      alert("Recovery message sent!");
      fetchCarts();
    } catch (e) {
      console.error(e);
      alert("Failed to send recovery message");
    }
    setRecovering(null);
  };


  const getOrigin = (cart: any) => cart.flightRoutes?.outbound?.[0]?.fromCode || cart.flightRoutes?.outBoundFlight?.flightDetails?.[0]?.fromCode || "";
  const getDestination = (cart: any) => {
    const ob = cart.flightRoutes?.outbound || cart.flightRoutes?.outBoundFlight?.flightDetails;
    const ib = cart.flightRoutes?.returnFlight || cart.flightRoutes?.inBoundFlight?.flightDetails;
    if (ib && ib.length > 0) return ib[0].fromCode;
    if (ob && ob.length > 0) return ob[ob.length - 1].toCode;
    return "";
  };
  const getDepDate = (cart: any) => cart.flightRoutes?.outbound?.[0]?.departureDate || cart.flightRoutes?.outBoundFlight?.flightDetails?.[0]?.departureDate || "";
  const getRetDate = (cart: any) => {
    const ib = cart.flightRoutes?.returnFlight || cart.flightRoutes?.inBoundFlight?.flightDetails;
    if (ib && ib.length > 0) return ib[0].departureDate;
    return "";
  };
  const getAirline = (cart: any) => cart.flightRoutes?.outbound?.[0]?.airline || cart.flightRoutes?.outBoundFlight?.flightDetails?.[0]?.airline || "N/A";

  const filteredCarts = carts.filter(cart => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = cart.contactDetails?.name?.toLowerCase().includes(searchLower);
    const emailMatch = cart.contactDetails?.email?.toLowerCase().includes(searchLower);
    const phoneMatch = cart.contactDetails?.phone?.toLowerCase().includes(searchLower);
    return nameMatch || emailMatch || phoneMatch;
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Abandoned Carts</h1>
      
      <div className="mb-4">
        <input 
          type="text" 
          placeholder="Search by name, email, or phone..." 
          className="border border-gray-300 p-2 rounded w-full max-w-md"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <p>Loading carts...</p>
      ) : carts.length === 0 ? (
        <p>No abandoned carts found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="py-3 px-4 text-left font-semibold text-gray-700">
                  Date
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">First Name</th>
                
                <th className="py-3 px-4 text-left font-semibold text-gray-700">City Pair</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Travel Dates</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">Airline</th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">
                  Contact
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">
                  Status
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-700">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCarts.map((cart) => (
                <tr
                  key={cart.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    {new Date(cart.createdAt).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    {cart.contactDetails?.name || "Unknown"}
                  </td>
                  
                                                      <td className="py-3 px-4">
                    {getOrigin(cart)} {getDestination(cart) ? `→ ${getDestination(cart)}` : ''}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                    <div>{getDepDate(cart)}</div>
                    {getRetDate(cart) && <div>{getRetDate(cart)}</div>}
                  </td>
                  <td className="py-3 px-4 font-semibold text-gray-700">
                    {getAirline(cart)}
                  </td>
                  <td className="py-3 px-4">
                    <div>{cart.contactDetails?.email}</div>
                    <div className="text-sm text-gray-500">
                      {cart.contactDetails?.phone}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-2 py-1 rounded text-xs font-bold ${cart.status === "ABANDONED" ? "bg-red-100 text-red-800" : cart.status === "PENDING" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
                    >
                      {cart.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {cart.status !== "PNR_CREATED" && (
                        <div className="flex items-center">
                      <button
                        onClick={() => handleRecover(cart.id)}
                        disabled={
                          recovering === cart.id || !cart.contactDetails?.phone
                        }
                        className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50 text-sm whitespace-nowrap"
                      >
                        {recovering === cart.id
                          ? "Sending..."
                          : "Recover via WhatsApp"}
                      </button>

                      <button
                        onClick={() => setSelectedCart(cart)}
                        className="ml-2 bg-gray-200 text-gray-700 hover:text-gray-900 px-3 py-2 rounded transition whitespace-nowrap font-medium text-sm"
                      >
                        View Details
                      </button>

                      <button
                        onClick={() => handleDelete(cart.id)}
                        disabled={deleting === cart.id}
                        className="ml-2 text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Delete Cart"
                      >
                        {deleting === cart.id ? "..." : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                        </div>
                    )}

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Details Modal */}
      {selectedCart && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-2xl font-bold text-gray-800">Abandoned Cart Details</h2>
              <button 
                onClick={() => setSelectedCart(null)}
                className="text-gray-500 hover:text-gray-800 text-2xl font-bold leading-none"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Primary Contact Section */}
              <section className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3 border-b border-blue-200 pb-2">Primary Contact</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">First Name</p>
                    <p className="font-medium text-gray-900">{selectedCart.contactDetails?.name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Name</p>
                    <p className="font-medium text-gray-900">{selectedCart.contactDetails?.surname || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{selectedCart.contactDetails?.email || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{selectedCart.contactDetails?.phone || "N/A"}</p>
                  </div>
                </div>
              </section>

              {/* Passenger Details Section */}
              {selectedCart.passengerDetails && Object.keys(selectedCart.passengerDetails).length > 0 ? (
                <section>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Passenger Details</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Render passenger entries dynamically */}
                    {Object.keys(selectedCart.passengerDetails)
                      .filter(key => key.includes("-title"))
                      .map((titleKey) => {
                        const passengerPrefix = titleKey.replace("-title", ""); // e.g. "Adult-0"
                        const pDetails = selectedCart.passengerDetails;
                        
                        return (
                          <div key={passengerPrefix} className="border border-gray-200 rounded p-4 bg-gray-50">
                            <h4 className="font-bold text-gray-700 mb-2 capitalize">{passengerPrefix.replace("-", " ")}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4 text-sm">
                              <div><p className="text-gray-500">Title:</p><p>{pDetails[`${passengerPrefix}-title`] || "-"}</p></div>
                              <div><p className="text-gray-500">First Name:</p><p>{pDetails[`${passengerPrefix}-firstName`] || "-"}</p></div>
                              <div><p className="text-gray-500">Last Name:</p><p>{pDetails[`${passengerPrefix}-lastName`] || "-"}</p></div>
                              <div><p className="text-gray-500">Gender:</p><p>{pDetails[`${passengerPrefix}-gender`] || "-"}</p></div>
                              <div><p className="text-gray-500">DOB:</p><p>{pDetails[`${passengerPrefix}-dob`] || "-"}</p></div>
                              <div><p className="text-gray-500">Passport No:</p><p>{pDetails[`${passengerPrefix}-passportNumber`] || "-"}</p></div>
                              <div><p className="text-gray-500">Passport Issuing Country:</p><p>{pDetails[`${passengerPrefix}-passportIssuingCountry`] || "-"}</p></div>
                              <div><p className="text-gray-500">Passport Issued:</p><p>{pDetails[`${passengerPrefix}-issueDate`] || "-"}</p></div>
                              <div><p className="text-gray-500">Passport Expiry:</p><p>{pDetails[`${passengerPrefix}-expire`] || "-"}</p></div>
                              <div><p className="text-gray-500">Frequent Flyer No:</p><p>{pDetails[`${passengerPrefix}-frequentFlyer`] || "-"}</p></div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </section>
              ) : (
                <section>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Passenger Details</h3>
                  <p className="text-gray-500 text-sm">No detailed passenger information was entered before abandonment.</p>
                </section>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
              <button 
                onClick={() => setSelectedCart(null)}
                className="bg-gray-800 text-white px-6 py-2 rounded hover:bg-gray-700 transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbandonedCarts;
